import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import {
	format,
	startOfWeek,
	endOfWeek,
	eachDayOfInterval,
	startOfMonth,
	endOfMonth,
	isSameDay,
} from 'date-fns';
import { classNames } from '../helpers/functions';
import {
	formatTimeRange,
	formatTimeFromMinutes,
	isSlotOnDate,
	getDateString,
} from './types';
import {
	ComputerDesktopIcon,
	TruckIcon,
	UserGroupIcon,
	CakeIcon,
	PresentationChartLineIcon,
	DeviceTabletIcon,
	ChevronRightIcon,
	PencilIcon,
	LockOpenIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
	CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { CurrentTimeIndicator, useCurrentTime } from './CurrentTimeIndicator';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

const iconMap = {
	monitor: ComputerDesktopIcon,
	car: TruckIcon,
	users: UserGroupIcon,
	coffee: CakeIcon,
	projector: PresentationChartLineIcon,
	laptop: DeviceTabletIcon,
};

// Grid runs from midnight to midnight (24 hours = 1440 minutes)
const GRID_START = 0;
const GRID_END = 24 * 60;
const GRID_TOTAL_MINUTES = GRID_END - GRID_START;

function SlotButton({
	slot,
	leftPercent,
	widthPercent,
	onRelease,
	onEdit,
	onView,
	onConfirm,
}) {
	const isGroupBlocked = slot.isGroupBlocked;
	const status = slot.status || 'pending';
	const isCompleted =
		status === 'completed' || new Date(slot.endTime) < new Date();
	const isConfirmed = status === 'confirmed';

	// Color scheme based on status:
	// - Gray: completed or past end time
	// - Green: confirmed
	// - Blue: pending/unconfirmed
	// - Light gray with dashed: group-blocked slots
	let buttonClasses;
	let textClasses;

	if (isGroupBlocked) {
		buttonClasses =
			'bg-gray-300/40 border border-gray-300 hover:bg-gray-300/50 hover:border-gray-400';
		textClasses = 'text-gray-600';
	} else if (isCompleted) {
		buttonClasses =
			'bg-gray-400/25 border border-gray-300 hover:bg-gray-400/35 hover:border-gray-400';
		textClasses = 'text-gray-600';
	} else if (isConfirmed) {
		buttonClasses =
			'bg-green-400/25 border border-green-200 hover:bg-green-400/35 hover:border-green-300';
		textClasses = 'text-green-700';
	} else {
		// Pending/unconfirmed - blue
		buttonClasses =
			'bg-blue-400/25 border border-blue-200 hover:bg-blue-400/35 hover:border-blue-300';
		textClasses = 'text-blue-700';
	}

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<button
					data-slot-button="true"
					className={classNames(
						'absolute top-1 bottom-1 rounded-md text-xs font-medium transition-all duration-200 flex items-center justify-center gap-1 px-2 z-[1]',
						buttonClasses,
					)}
					style={{
						left: `calc(${leftPercent}% + 2px)`,
						width: `calc(${widthPercent}% - 4px)`,
					}}
					title={
						isGroupBlocked
							? `Blocked by group booking: ${formatTimeRange(slot)}`
							: formatTimeRange(slot)
					}
				>
					<span
						className={classNames(
							'text-xs font-medium truncate px-1',
							textClasses,
						)}
					>
						{slot.bookedBy}
					</span>
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Content className="z-50 bg-white border shadow-lg min-w-[8rem] overflow-hidden rounded-md p-1 text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 bg-popover border shadow-lg">
				{!isGroupBlocked && (
					<DropdownMenu.Item
						onClick={onView}
						className="gap-2 cursor-pointer relative flex select-none items-center rounded-sm px-2 py-1.5 text-xs outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground gap-2 cursor-pointer"
					>
						<MagnifyingGlassIcon className="w-4 h-4" />
						{'View'}
					</DropdownMenu.Item>
				)}

				{!isGroupBlocked && (
					<DropdownMenu.Item
						onClick={onEdit}
						className="gap-2 cursor-pointer relative flex select-none items-center rounded-sm px-2 py-1.5 text-xs outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground gap-2 cursor-pointer"
					>
						<PencilIcon className="w-4 h-4" />
						Edit
					</DropdownMenu.Item>
				)}

				{/* Show Confirm option only for pending (unconfirmed) reservations */}
				{/*!isGroupBlocked && !isConfirmed && !isCompleted && (
					<DropdownMenu.Item
						onClick={onConfirm}
						className="gap-2 cursor-pointer relative flex select-none items-center rounded-sm px-2 py-1.5 text-xs outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground text-green-600 hover:text-green-700"
					>
						<CheckCircleIcon className="w-4 h-4" />
						Confirm
					</DropdownMenu.Item>
				)*/}

				<DropdownMenu.Item
					onClick={onRelease}
					className="gap-2 cursor-pointer relative flex select-none items-center rounded-sm px-2 py-1.5 text-xs outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground gap-2 cursor-pointer"
				>
					<LockOpenIcon className="w-4 h-4" />
					{isGroupBlocked ? 'Remove from Booking' : 'Delete'}
				</DropdownMenu.Item>
			</DropdownMenu.Content>
		</DropdownMenu.Root>
	);
}

// Split gaps into 30-minute segments aligned to half-hour boundaries
const SLOT_SEGMENT_DURATION = 30; // 30 minutes per visual slot

function splitGapsIntoSegments(gaps) {
	const segments = [];

	for (const gap of gaps) {
		// Find the next 30-minute boundary from gap start
		const nextBoundary =
			Math.ceil(gap.start / SLOT_SEGMENT_DURATION) * SLOT_SEGMENT_DURATION;

		// If gap doesn't start at a 30-minute boundary, check if there's a partial segment
		if (gap.start < nextBoundary && nextBoundary <= gap.end) {
			const partialDuration = nextBoundary - gap.start;
			// Include partial segment if it's at least 15 minutes
			if (partialDuration >= 15) {
				segments.push({ start: gap.start, end: nextBoundary });
			}
		}

		// Now add 30-minute aligned segments
		let current = nextBoundary;

		// If gap starts exactly at a boundary or nextBoundary is beyond gap, start from gap.start
		if (gap.start === nextBoundary || nextBoundary > gap.end) {
			current = gap.start;
		}

		while (current < gap.end) {
			const segmentEnd = Math.min(current + SLOT_SEGMENT_DURATION, gap.end);
			const duration = segmentEnd - current;

			// Only add segment if it's at least 15 minutes and we haven't already added it as partial
			if (duration >= 15 && current >= nextBoundary) {
				segments.push({ start: current, end: segmentEnd });
			} else if (duration >= 15 && nextBoundary > gap.end) {
				// Gap is entirely before next boundary
				segments.push({ start: current, end: segmentEnd });
				break;
			}
			current = segmentEnd;
		}
	}

	return segments;
}

// Empty slot visual indicator (shows "+ Book" on hover)
function EmptySlotIndicator({ segment, onMouseDown }) {
	const leftPercent = ((segment.start - GRID_START) / GRID_TOTAL_MINUTES) * 100;
	const widthPercent =
		((segment.end - segment.start) / GRID_TOTAL_MINUTES) * 100;

	// Pass the segment's start time to handleMouseDown so clicking anywhere
	// in the segment uses the segment's start time, not the pixel position
	const handleSegmentMouseDown = (e) => {
		onMouseDown(e, segment.start);
	};

	return (
		<div
			className="absolute top-1 bottom-1 rounded-md transition-all duration-200 hover:bg-indigo-50 hover:border-2 hover:border-dashed hover:border-indigo-300 group cursor-pointer"
			style={{
				left: `calc(${leftPercent}% + 2px)`,
				width: `calc(${widthPercent}% - 4px)`,
			}}
			title={`Click to book: ${formatTimeFromMinutes(segment.start)} - ${formatTimeFromMinutes(segment.end)} (drag to extend)`}
			onMouseDown={handleSegmentMouseDown}
		>
			<span className="hidden group-hover:flex items-center justify-center h-full text-xs text-indigo-600 font-medium">
				+ Book
			</span>
		</div>
	);
}

// Draggable timeline row component for drag-to-select time ranges
function DraggableTimelineRow({
	asset,
	date,
	gaps,
	slotsForDate,
	onEmptyAreaClick,
	onSlotRelease,
	onSlotEdit,
	onSlotView,
	onSlotConfirm,
	pendingBooking,
}) {
	const rowRef = useRef(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState(null);
	const [dragEnd, setDragEnd] = useState(null);

	// Convert pixel position to minutes, snapped to 30-minute intervals
	const pixelToMinutes = useCallback((pixelX) => {
		if (!rowRef.current) return 0;
		const rect = rowRef.current.getBoundingClientRect();
		const relativeX = pixelX - rect.left;
		const percentage = relativeX / rect.width;
		const rawMinutes = GRID_START + percentage * GRID_TOTAL_MINUTES;
		// Snap to 30-minute intervals (e.g., 5:00, 5:30, 6:00)
		return Math.round(rawMinutes / 30) * 30;
	}, []);

	// Check if a time range overlaps with any booked slot
	const isTimeRangeAvailable = useCallback(
		(startMin, endMin) => {
			for (const slot of slotsForDate) {
				const slotStart = slot.startHour * 60 + (slot.startMinutes || 0);
				const slotEnd = slotStart + slot.durationMinutes;
				// Check for overlap
				if (startMin < slotEnd && endMin > slotStart) {
					return false;
				}
			}
			return true;
		},
		[slotsForDate],
	);

	// Clamp selection to available gaps
	const clampToGaps = useCallback(
		(startMin, endMin) => {
			// Find which gap(s) the selection falls into
			let clampedStart = startMin;
			let clampedEnd = endMin;

			for (const gap of gaps) {
				// If start is in this gap
				if (startMin >= gap.start && startMin < gap.end) {
					clampedStart = Math.max(startMin, gap.start);
					// Clamp end to gap boundary or next booked slot
					clampedEnd = Math.min(endMin, gap.end);
					break;
				}
			}

			return { start: clampedStart, end: clampedEnd };
		},
		[gaps],
	);

	const handleMouseDown = useCallback(
		(e, segmentStartMinutes = null) => {
			// Only handle left click
			if (e.button !== 0) return;

			// Don't start drag if clicking on a slot button
			if (e.target.closest('[data-slot-button]')) return;

			// Use the segment's start time if provided, otherwise calculate from pixel position
			// This ensures clicking anywhere in a segment uses that segment's start time
			const minutes =
				segmentStartMinutes !== null
					? segmentStartMinutes
					: pixelToMinutes(e.clientX);

			// Check if clicking in an available area
			if (!isTimeRangeAvailable(minutes, minutes + 1)) return;

			setIsDragging(true);
			setDragStart(minutes);
			setDragEnd(minutes);

			e.preventDefault();
		},
		[pixelToMinutes, isTimeRangeAvailable],
	);

	const handleMouseMove = useCallback(
		(e) => {
			if (!isDragging) return;

			const minutes = pixelToMinutes(e.clientX);
			setDragEnd(minutes);
		},
		[isDragging, pixelToMinutes],
	);

	const handleMouseUp = useCallback(
		(e) => {
			if (!isDragging) return;

			const endMinutes = pixelToMinutes(e.clientX);

			// Determine start and end (handle dragging left or right)
			let selStart = Math.min(dragStart, endMinutes);
			let selEnd = Math.max(dragStart, endMinutes);

			// Ensure minimum duration of 30 minutes
			if (selEnd - selStart < 30) {
				selEnd = selStart + 30; // Default to 30-minute slot
			}

			// Clamp to available gaps
			const clamped = clampToGaps(selStart, selEnd);

			// Ensure the selection is valid
			if (
				clamped.end > clamped.start &&
				isTimeRangeAvailable(clamped.start, clamped.end)
			) {
				onEmptyAreaClick(asset.id, clamped.start, clamped.end, date);
			}

			setIsDragging(false);
			setDragStart(null);
			setDragEnd(null);
		},
		[
			isDragging,
			dragStart,
			pixelToMinutes,
			clampToGaps,
			isTimeRangeAvailable,
			onEmptyAreaClick,
			asset.id,
			date,
		],
	);

	// Add global mouse listeners when dragging
	useEffect(() => {
		if (isDragging) {
			const handleGlobalMouseMove = (e) => handleMouseMove(e);
			const handleGlobalMouseUp = (e) => handleMouseUp(e);

			document.addEventListener('mousemove', handleGlobalMouseMove);
			document.addEventListener('mouseup', handleGlobalMouseUp);

			return () => {
				document.removeEventListener('mousemove', handleGlobalMouseMove);
				document.removeEventListener('mouseup', handleGlobalMouseUp);
			};
		}
	}, [isDragging, handleMouseMove, handleMouseUp]);

	// Calculate selection preview
	const selectionPreview = useMemo(() => {
		if (!isDragging || dragStart === null || dragEnd === null) return null;

		let selStart = Math.min(dragStart, dragEnd);
		let selEnd = Math.max(dragStart, dragEnd);

		// Ensure minimum width of 30 minutes
		if (selEnd - selStart < 30) {
			selEnd = selStart + 30;
		}

		// Clamp to gaps
		const clamped = clampToGaps(selStart, selEnd);

		const leftPercent =
			((clamped.start - GRID_START) / GRID_TOTAL_MINUTES) * 100;
		const widthPercent =
			((clamped.end - clamped.start) / GRID_TOTAL_MINUTES) * 100;

		return {
			left: leftPercent,
			width: widthPercent,
			start: clamped.start,
			end: clamped.end,
		};
	}, [isDragging, dragStart, dragEnd, clampToGaps]);

	// Split gaps into 30-minute segments for visual indicators
	const segments = useMemo(() => splitGapsIntoSegments(gaps), [gaps]);

	// Check if this row should show a pending booking indicator
	const pendingBookingForRow = useMemo(() => {
		if (!pendingBooking) return null;

		const {
			assetIds,
			startMinutes,
			endMinutes,
			date: bookingDate,
		} = pendingBooking;

		// Check if the booking date matches this row's date
		const bookingDateStr = bookingDate
			? getDateString(new Date(bookingDate))
			: null;
		const rowDateStr = getDateString(date);
		if (bookingDateStr !== rowDateStr) return null;

		// Check if this asset is in the pending booking's asset list
		// Handle both regular assets and group assets
		const assetId = asset.id.startsWith('group-') ? null : asset.id;
		const isAssetIncluded = assetIds && assetIds.includes(assetId);

		if (!isAssetIncluded) return null;

		// Calculate position
		const leftPercent =
			((startMinutes - GRID_START) / GRID_TOTAL_MINUTES) * 100;
		const widthPercent =
			((endMinutes - startMinutes) / GRID_TOTAL_MINUTES) * 100;

		return {
			left: leftPercent,
			width: widthPercent,
			start: startMinutes,
			end: endMinutes,
		};
	}, [pendingBooking, asset.id, date]);

	return (
		<div ref={rowRef} className="absolute inset-0">
			{/* Empty slot indicators (show "+ Book" on hover) - 30-minute segments */}
			{!isDragging &&
				segments.map((segment, index) => (
					<EmptySlotIndicator
						key={`segment-${index}`}
						segment={segment}
						onMouseDown={handleMouseDown}
					/>
				))}

			{/* Selection preview while dragging */}
			{selectionPreview && (
				<div
					className="absolute top-1 bottom-1 rounded-md bg-indigo-100 border-2 border-dashed border-indigo-400 flex items-center justify-center z-[2] pointer-events-none"
					style={{
						left: `calc(${selectionPreview.left}% + 2px)`,
						width: `calc(${selectionPreview.width}% - 4px)`,
					}}
				>
					<span className="text-xs text-indigo-700 font-medium bg-white/80 px-2 py-0.5 rounded">
						{formatTimeFromMinutes(selectionPreview.start)} -{' '}
						{formatTimeFromMinutes(selectionPreview.end)}
					</span>
				</div>
			)}

			{/* Pending booking indicator (shown while slider is open) */}
			{pendingBookingForRow && !isDragging && (
				<div
					className="absolute top-1 bottom-1 rounded-md bg-indigo-200/50 border-2 border-dashed border-indigo-400 flex items-center justify-center z-[1] pointer-events-none animate-pulse"
					style={{
						left: `calc(${pendingBookingForRow.left}% + 2px)`,
						width: `calc(${pendingBookingForRow.width}% - 4px)`,
					}}
				>
					<span className="text-xs text-indigo-700 font-medium bg-white/80 px-2 py-0.5 rounded">
						{formatTimeFromMinutes(pendingBookingForRow.start)} -{' '}
						{formatTimeFromMinutes(pendingBookingForRow.end)}
					</span>
				</div>
			)}

			{/* Booked slots */}
			{slotsForDate.map((slot) => {
				const slotStart = slot.startHour * 60 + (slot.startMinutes || 0);
				const leftPercent =
					((slotStart - GRID_START) / GRID_TOTAL_MINUTES) * 100;
				const widthPercent = (slot.durationMinutes / GRID_TOTAL_MINUTES) * 100;
				return (
					<SlotButton
						key={slot.id}
						slot={slot}
						leftPercent={leftPercent}
						widthPercent={widthPercent}
						onView={() => onSlotView(slot.reservationId)}
						onRelease={() => onSlotRelease(asset.id, slot.id)}
						onEdit={() => onSlotEdit(asset.id, slot)}
						onConfirm={() => onSlotConfirm(slot.reservationId)}
					/>
				);
			})}
		</div>
	);
}

// Helper to find gaps between booked slots for a specific date
function findGaps(slots, date) {
	const bookedSlots = slots.filter((s) => s.isBooked && isSlotOnDate(s, date));
	if (bookedSlots.length === 0) return [{ start: GRID_START, end: GRID_END }];

	const positions = bookedSlots
		.map((slot) => ({
			start: slot.startHour * 60 + (slot.startMinutes || 0),
			end:
				slot.startHour * 60 + (slot.startMinutes || 0) + slot.durationMinutes,
		}))
		.sort((a, b) => a.start - b.start);

	const gaps = [];

	if (positions[0].start > GRID_START) {
		gaps.push({ start: GRID_START, end: positions[0].start });
	}

	for (let i = 0; i < positions.length - 1; i++) {
		if (positions[i].end < positions[i + 1].start) {
			gaps.push({ start: positions[i].end, end: positions[i + 1].start });
		}
	}

	if (positions[positions.length - 1].end < GRID_END) {
		gaps.push({ start: positions[positions.length - 1].end, end: GRID_END });
	}

	return gaps;
}

export function TimelineGrid({
	assets,
	selectedDate,
	viewMode,
	onSlotRelease,
	onSlotEdit,
	onSlotView,
	onSlotConfirm,
	onEmptyAreaClick,
	onDateClick,
	expandedGroups,
	onToggleGroup,
	onRemoveAsset,
	onEditGroup,
	zoomLevel = 2400, // Default zoom level in pixels for day view
	pendingBooking = null, // Data for the booking being created in the slider
}) {
	const scrollRef = useRef(null);
	const currentMinutes = useCurrentTime();
	const today = new Date();

	// Calculate dates to display based on view mode
	const dates = useMemo(() => {
		switch (viewMode) {
			case 'day':
				return [selectedDate];
			case 'week': {
				const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
				const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
				return eachDayOfInterval({ start, end });
			}
			case 'month': {
				const start = startOfMonth(selectedDate);
				const end = endOfMonth(selectedDate);
				return eachDayOfInterval({ start, end });
			}
		}
	}, [selectedDate, viewMode]);

	// Auto-scroll for day view
	useEffect(() => {
		if (viewMode !== 'day' || !scrollRef.current) return;

		const isToday = isSameDay(selectedDate, today);
		if (!isToday) return;

		const isInRange =
			currentMinutes >= GRID_START && currentMinutes <= GRID_END;
		if (!isInRange) return;

		const timeProgress = (currentMinutes - GRID_START) / GRID_TOTAL_MINUTES;
		const containerWidth = scrollRef.current.clientWidth;
		const contentWidth = scrollRef.current.scrollWidth;

		if (contentWidth > containerWidth) {
			const targetScrollLeft =
				timeProgress * contentWidth - containerWidth * 0.25;
			scrollRef.current.scrollLeft = Math.max(
				0,
				Math.min(targetScrollLeft, contentWidth - containerWidth),
			);
		}
	}, [currentMinutes, viewMode, selectedDate]);

	// Flatten assets
	const flattenedAssets = [];
	assets.forEach((asset) => {
		flattenedAssets.push(asset);
		if (asset.subAssets && expandedGroups[asset.id]) {
			asset.subAssets.forEach((subAsset) => {
				flattenedAssets.push({ ...subAsset, isSubAsset: true });
			});
		}
	});

	// Generate hour markers for labels (every hour)
	const hourLabels = [];
	for (let h = 0; h <= 24; h++) {
		hourLabels.push(h);
	}

	// Generate 30-minute markers for grid lines
	const halfHourMarkers = [];
	for (let m = 0; m <= 24 * 60; m += 30) {
		halfHourMarkers.push(m);
	}

	// Format hour to AM/PM
	const formatHourAmPm = (hour) => {
		if (hour === 0 || hour === 24) return '12AM';
		if (hour === 12) return '12PM';
		if (hour < 12) return `${hour}AM`;
		return `${hour - 12}PM`;
	};

	// Day view width is based on zoom level
	const dayViewWidth = zoomLevel;
	const cellWidthClass =
		viewMode === 'week'
			? 'min-w-[120px]'
			: viewMode === 'month'
				? 'min-w-[40px]'
				: '';
	// For day view, we use inline style since Tailwind can't handle dynamic values
	const cellWidthStyle =
		viewMode === 'day' ? { minWidth: `${dayViewWidth}px` } : {};

	return (
		<div className="bg-white rounded-xl border overflow-hidden">
			<div className="flex">
				{/* Static asset column */}
				<div className="w-72 shrink-0 border-r bg-white z-10">
					{/* Header */}
					<div className="p-3 font-semibold text-sm text-gray-500 border-b bg-gray-50 h-12 flex items-center">
						Asset
					</div>
					{/* Asset rows */}
					{flattenedAssets.map((asset, assetIndex) => {
						const Icon = iconMap[asset.icon] || ComputerDesktopIcon;
						const hasSubAssets = asset.subAssets && asset.subAssets.length > 0;
						const isExpanded = expandedGroups[asset.id];

						return (
							<div
								key={asset.id}
								className={classNames(
									'p-3 border-b last:border-b-0 flex items-center gap-3 h-20',
									assetIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50',
									asset.isSubAsset && 'bg-gray-100 pl-8',
								)}
							>
								{hasSubAssets ? (
									<button
										onClick={() => onToggleGroup(asset.id)}
										className="p-2 rounded-lg bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-colors"
									>
										<ChevronRightIcon
											className={classNames(
												'w-4 h-4 transition-transform',
												isExpanded && 'rotate-90',
											)}
										/>
									</button>
								) : (
									<div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
										<Icon className="w-4 h-4" />
									</div>
								)}
								<div className="min-w-0 flex-1">
									<p className="font-medium text-sm truncate text-gray-900">
										{asset.name}
									</p>
									<p className="text-xs text-gray-500 truncate">
										{hasSubAssets
											? `${asset.subAssets.length} sub-assets`
											: asset.description}
									</p>
								</div>
								{/* Edit button for groups */}
								{onEditGroup && asset.isGroup && asset.groupId && (
									<button
										onClick={() => onEditGroup(asset.groupId)}
										className="p-1 rounded hover:bg-indigo-100 text-gray-400 hover:text-indigo-600 transition-colors"
										title="Edit group"
									>
										<PencilIcon className="w-4 h-4" />
									</button>
								)}
								{onRemoveAsset && !asset.isSubAsset && (
									<button
										onClick={() => onRemoveAsset(asset.id)}
										className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
										title={
											asset.isGroup
												? 'Delete group'
												: 'Remove from booking list'
										}
									>
										<XMarkIcon className="w-4 h-4" />
									</button>
								)}
							</div>
						);
					})}
				</div>

				{/* Scrollable timeline */}
				<div className="flex-1 overflow-x-auto" ref={scrollRef}>
					<div
						className={classNames(
							'flex',
							viewMode === 'day' && 'min-w-[600px]',
						)}
					>
						{dates.map((date) => {
							const isToday = isSameDay(date, today);
							const dateStr = getDateString(date);

							console.log('GRID_START: ', GRID_START, GRID_TOTAL_MINUTES);

							return (
								<div
									key={dateStr}
									className={classNames('flex-1', cellWidthClass)}
									style={cellWidthStyle}
								>
									{/* Date header */}
									<div
										className={classNames(
											'border-b bg-gray-50 h-12 flex items-center justify-center text-sm font-medium relative',
											isToday && 'bg-indigo-50',
										)}
									>
										{viewMode === 'day' ? (
											<div className="flex-1 relative h-full">
												{isToday && (
													<CurrentTimeIndicator
														gridStartMinutes={GRID_START}
														gridTotalMinutes={GRID_TOTAL_MINUTES}
													/>
												)}
												{hourLabels.map((hour) => {
													const leftPercent =
														((hour * 60 - GRID_START) / GRID_TOTAL_MINUTES) *
														100;
													return (
														<div
															key={hour}
															className="absolute top-0 h-full flex items-center text-[10px] text-gray-400"
															style={{
																left: `${leftPercent}%`,
																transform: 'translateX(-50%)',
															}}
														>
															{formatHourAmPm(hour)}
														</div>
													);
												})}
											</div>
										) : (
											<div className="text-center">
												<div
													className={classNames(
														'text-xs',
														isToday && 'text-indigo-600 font-bold',
													)}
												>
													{format(date, viewMode === 'week' ? 'EEE' : 'd')}
												</div>
												{viewMode === 'week' && (
													<div
														className={classNames(
															'text-xs',
															isToday && 'text-indigo-600 font-bold',
														)}
													>
														{format(date, 'd')}
													</div>
												)}
											</div>
										)}
									</div>

									{/* Asset timeline rows */}
									{flattenedAssets.map((asset, assetIndex) => {
										// Filter slots for this date and adjust for multi-day reservations
										const slotsForDate = asset.slots
											.filter((s) => s.isBooked && isSlotOnDate(s, date))
											.map((slot) => {
												// For multi-day reservations, adjust start time and duration
												// based on which day of the reservation we're rendering
												if (!slot.endDate || slot.startDate === slot.endDate) {
													// Single-day reservation - no adjustment needed
													return slot;
												}

												const currentDateStr = getDateString(date);
												const isStartDay = currentDateStr === slot.startDate;
												const isEndDay = currentDateStr === slot.endDate;

												if (isStartDay) {
													// On start day: show from start time until midnight
													const startMinutes =
														slot.startHour * 60 + (slot.startMinutes || 0);
													const minutesUntilMidnight = GRID_END - startMinutes;
													return {
														...slot,
														durationMinutes: minutesUntilMidnight,
													};
												} else if (isEndDay) {
													// On end day: show from midnight until end time
													// Calculate end time from the original reservation
													const originalStartMinutes =
														slot.startHour * 60 + (slot.startMinutes || 0);
													const originalEndMinutes =
														originalStartMinutes + slot.durationMinutes;
													// End time is the total minutes mod 1440 (minutes in a day)
													const endMinutesOfDay =
														originalEndMinutes % GRID_END || GRID_END;
													return {
														...slot,
														startHour: 0,
														startMinutes: 0,
														durationMinutes: endMinutesOfDay,
													};
												} else {
													// Middle day: show entire day
													return {
														...slot,
														startHour: 0,
														startMinutes: 0,
														durationMinutes: GRID_END,
													};
												}
											});
										const gaps = findGaps(asset.slots, date);

										return (
											<div
												key={`${asset.id}-${dateStr}`}
												className={classNames(
													'relative h-20 border-b last:border-b-0',
													assetIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50',
													asset.isSubAsset && 'bg-gray-100',
												)}
											>
												{/* Current time indicator for day view */}
												{viewMode === 'day' && isToday && (
													<CurrentTimeIndicator
														gridStartMinutes={GRID_START}
														gridTotalMinutes={GRID_TOTAL_MINUTES}
													/>
												)}

												{/* Hour grid lines for day view */}
												{viewMode === 'day' &&
													halfHourMarkers.map((minutes) => {
														const leftPercent =
															((minutes - GRID_START) / GRID_TOTAL_MINUTES) *
															100;
														const isHour = minutes % 60 === 0;
														return (
															<div
																key={minutes}
																className={classNames(
																	'absolute top-0 h-full border-l',
																	isHour
																		? 'border-gray-300/30'
																		: 'border-gray-300/20',
																)}
																style={{ left: `${leftPercent}%` }}
															/>
														);
													})}

												{/* Day view: draggable timeline with slots */}
												{viewMode === 'day' && (
													<DraggableTimelineRow
														asset={asset}
														date={date}
														gaps={gaps}
														slotsForDate={slotsForDate}
														onEmptyAreaClick={onEmptyAreaClick}
														onSlotRelease={onSlotRelease}
														onSlotEdit={onSlotEdit}
														onSlotView={onSlotView}
														onSlotConfirm={onSlotConfirm}
														pendingBooking={pendingBooking}
													/>
												)}

												{/* Week/Month view: show booking indicator or clickable cell */}
												{viewMode !== 'day' && (
													<button
														onClick={() => onDateClick(date)}
														className={classNames(
															'absolute inset-1 rounded transition-all',
															slotsForDate.length > 0
																? (() => {
																		// Determine color based on slot statuses
																		const hasConfirmed = slotsForDate.some(
																			(s) => s.status === 'confirmed',
																		);
																		const allCompleted = slotsForDate.every(
																			(s) =>
																				s.status === 'completed' ||
																				new Date(s.endTime) < new Date(),
																		);
																		if (allCompleted)
																			return 'bg-gray-100 border border-gray-300';
																		if (hasConfirmed)
																			return 'bg-green-100 border border-green-200';
																		return 'bg-blue-100 border border-blue-200';
																	})()
																: 'hover:bg-indigo-50 hover:border hover:border-dashed hover:border-indigo-300',
														)}
														title={
															slotsForDate.length > 0
																? slotsForDate.map((s) => s.bookedBy).join(', ')
																: 'Click to book'
														}
													>
														{slotsForDate.length > 0 && (
															<span
																className={classNames(
																	'text-xs font-medium truncate px-1',
																	(() => {
																		const hasConfirmed = slotsForDate.some(
																			(s) => s.status === 'confirmed',
																		);
																		const allCompleted = slotsForDate.every(
																			(s) =>
																				s.status === 'completed' ||
																				new Date(s.endTime) < new Date(),
																		);
																		if (allCompleted) return 'text-gray-600';
																		if (hasConfirmed) return 'text-green-700';
																		return 'text-blue-700';
																	})(),
																)}
															>
																{slotsForDate.length > 1
																	? `${slotsForDate.length} bookings`
																	: slotsForDate[0].bookedBy}
															</span>
														)}
													</button>
												)}
											</div>
										);
									})}
								</div>
							);
						})}
					</div>
				</div>
			</div>

			{/* Legend */}
			<div className="flex items-center gap-6 p-3 border-t bg-gray-50 text-xs">
				<div className="flex items-center gap-2">
					<div className="w-4 h-4 rounded bg-blue-100 border border-blue-200" />
					<span className="text-gray-500">Pending</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="w-4 h-4 rounded bg-green-100 border border-green-200" />
					<span className="text-gray-500">Confirmed</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="w-4 h-4 rounded bg-gray-200 border border-gray-300" />
					<span className="text-gray-500">Completed</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="w-0.5 h-4 bg-indigo-400" />
					<span className="text-gray-500">Current Time</span>
				</div>
				<p className="text-gray-500 ml-auto">
					Click and drag on empty areas to create bookings
				</p>
			</div>
		</div>
	);
}
